import type { FC } from "react";
import { Form, Radio } from "antd";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import type { DashboardSelectOption, UserFormMode } from "../Types/UserTypes";
import { SOCIAL_LISTENING_FEATURE } from "../Utils/UserConstants";
import RequiredLabel from "./RequiredLabel";

const NAME_PATTERN = /^[A-Za-z\s]+$/;
const USERNAME_PATTERN = /^[a-zA-Z0-9.]{8,}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMMON_KEY = "userManagement.modals.common";

interface UserFormFieldsProps {
  mode: UserFormMode;
  isSocialListeningEnabled: boolean;
  dashboardOptions: DashboardSelectOption[];
  isDashboardsLoading: boolean;
  onDashboardChange: (_value: string[]) => void;
}

const UserFormFields: FC<UserFormFieldsProps> = ({
  mode,
  isSocialListeningEnabled,
  dashboardOptions,
  isDashboardsLoading,
  onDashboardChange,
}) => {
  const { t } = useTranslation();
  const isAddMode = mode === "add";
  const itemClassName = isAddMode ? "form-item error-message" : "form-item";

  const requiredRule = (messageKey: string) => ({ required: true, message: t(messageKey) });
  const patternRule = (pattern: RegExp, messageKey: string) => ({ pattern, message: t(messageKey) });

  const validateEmail = (_: unknown, value?: string) => {
    if (!value) {
      return Promise.reject(new Error(t(`${COMMON_KEY}.emailRequired`)));
    }
    return EMAIL_PATTERN.test(value)
      ? Promise.resolve()
      : Promise.reject(new Error(t(`${COMMON_KEY}.emailInvalid`)));
  };

  return (
    <>
      <div className="form-row">
        <Form.Item
          name="firstName"
          label={<RequiredLabel text={t(`${COMMON_KEY}.firstNameLabel`)} />}
          className={itemClassName}
          rules={[
            requiredRule(`${COMMON_KEY}.firstNameRequired`),
            ...(isAddMode ? [patternRule(NAME_PATTERN, `${COMMON_KEY}.firstNameAlpha`)] : []),
          ]}
        >
          <StyledTextInput placeholder={t(`${COMMON_KEY}.firstNamePlaceholder`)} />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={
            isAddMode ? (
              <RequiredLabel text={t(`${COMMON_KEY}.lastNameLabel`)} />
            ) : (
              t(`${COMMON_KEY}.lastNameLabel`)
            )
          }
          className={itemClassName}
          rules={
            isAddMode
              ? [
                  requiredRule(`${COMMON_KEY}.lastNameRequired`),
                  patternRule(NAME_PATTERN, `${COMMON_KEY}.lastNameAlpha`),
                ]
              : undefined
          }
        >
          <StyledTextInput placeholder={t(`${COMMON_KEY}.lastNamePlaceholder`)} />
        </Form.Item>
      </div>

      <Form.Item
        name="username"
        label={<RequiredLabel text={t(`${COMMON_KEY}.usernameLabel`)} />}
        className={itemClassName}
        rules={[
          requiredRule(`${COMMON_KEY}.usernameRequired`),
          ...(isAddMode ? [patternRule(USERNAME_PATTERN, `${COMMON_KEY}.usernameRule`)] : []),
        ]}
      >
        <StyledTextInput placeholder={t(`${COMMON_KEY}.usernamePlaceholder`)} />
      </Form.Item>

      <Form.Item
        name="email"
        label={t(`${COMMON_KEY}.emailLabel`)}
        className={itemClassName}
        rules={isAddMode ? [{ validator: validateEmail }] : undefined}
      >
        <StyledTextInput placeholder={t(`${COMMON_KEY}.emailPlaceholder`)} disabled={!isAddMode} />
      </Form.Item>

      <Form.Item
        name="role"
        label={<RequiredLabel text={t(`${COMMON_KEY}.roleLabel`)} />}
        className={itemClassName}
        rules={[requiredRule(`${COMMON_KEY}.roleRequired`)]}
      >
        <StyledDropdown
          placeholder={t(`${COMMON_KEY}.rolePlaceholder`)}
          options={[{ label: t("userManagement.role.viewer"), value: "viewer" }]}
          popupClassName="custom-dropdown-menu"
        />
      </Form.Item>

      {!isAddMode && (
        <Form.Item
          name="status"
          label={<RequiredLabel text={t("userManagement.modals.editUser.statusLabel")} />}
          className={itemClassName}
          rules={[requiredRule("userManagement.modals.editUser.statusRequired")]}
        >
          <Radio.Group>
            <Radio value="active">{t("userManagement.status.active")}</Radio>
            <Radio value="inactive">{t("userManagement.status.inactive")}</Radio>
          </Radio.Group>
        </Form.Item>
      )}

      {isSocialListeningEnabled && (
        <Form.Item name="features" label={t(`${COMMON_KEY}.featureAccessLabel`)} className={itemClassName}>
          <StyledDropdown
            mode="multiple"
            placeholder={t(`${COMMON_KEY}.featurePlaceholder`)}
            options={[
              { label: t(`${COMMON_KEY}.socialListeningAgent`), value: SOCIAL_LISTENING_FEATURE },
            ]}
            showSearch={false}
            maxTagCount={2}
            className="filter-dropdown"
            popupClassName="filter-dropdown-menu"
          />
        </Form.Item>
      )}

      <Form.Item
        name="dashboardAccess"
        label={<RequiredLabel text={t(`${COMMON_KEY}.dashboardAccessLabel`)} />}
        className={itemClassName}
        rules={[requiredRule(`${COMMON_KEY}.dashboardRequired`)]}
      >
        <StyledDropdown
          mode="multiple"
          placeholder={t(`${COMMON_KEY}.dashboardPlaceholder`)}
          onChange={value => onDashboardChange(value as string[])}
          options={dashboardOptions}
          showSearch={false}
          maxTagCount={2}
          className="filter-dropdown"
          popupClassName="filter-dropdown-menu"
          disabled={isAddMode && isDashboardsLoading}
        />
      </Form.Item>

      {isAddMode && (
        <Form.Item name="sendInvitation" valuePropName="checked" className="form-item-invitation">
          <span>{t("userManagement.modals.addUser.invitationNote")}</span>
        </Form.Item>
      )}
    </>
  );
};

export default UserFormFields;
